// src/firestore.js
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query as fsQuery,
  where,
  orderBy,
  limit,
  startAfter,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Get all documents in a user subcollection
 * Path: users/{uid}/{collectionName}/*
 */
export const getAllItems = async (uid, collectionName) => {
  if (!uid) return [];

  const colRef = collection(db, 'users', uid, collectionName);
  const snapshot = await getDocs(colRef);

  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
};

/**
 * Add new document
 * Path: users/{uid}/{collectionName}/(autoId)
 */
export const addItem = async (uid, collectionName, data) => {
  if (!uid) throw new Error('User not authenticated');

  const colRef = collection(db, 'users', uid, collectionName);
  const docRef = await addDoc(colRef, data);

  return { id: docRef.id, ...data };
};

/**
 * Update existing document (safe)
 * - If doc exists: updateDoc
 * - If doc does not exist: setDoc merge (prevents "No document to update")
 */
export const updateItem = async (uid, collectionName, docId, data) => {
  if (!uid) throw new Error('User not authenticated');

  const ref = doc(db, 'users', uid, collectionName, docId);

  // ✅ Prevent "No document to update" by checking existence
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, data, { merge: true });
    return;
  }

  await updateDoc(ref, data);
};

/**
 * Upsert (create or merge) any document (optional helper)
 */
export const upsertItem = async (uid, collectionName, docId, data) => {
  if (!uid) throw new Error('User not authenticated');

  const ref = doc(db, 'users', uid, collectionName, docId);
  await setDoc(ref, data, { merge: true });
};

/**
 * Delete document
 */
export const deleteItem = async (uid, collectionName, docId) => {
  if (!uid) throw new Error('User not authenticated');

  const ref = doc(db, 'users', uid, collectionName, docId);
  await deleteDoc(ref);
};

/**
 * Load singleton doc
 * Path: users/{uid}/{collectionName}/main
 * Returns default if missing
 */
export const loadUserSingleDoc = async (uid, collectionName, defaultData = {}) => {
  if (!uid) return defaultData;

  const ref = doc(db, 'users', uid, collectionName, 'main');
  const snap = await getDoc(ref);

  if (!snap.exists()) return defaultData;
  return snap.data();
};

/**
 * Save singleton doc (create or update / merge)
 * Path: users/{uid}/{collectionName}/main
 */
export const saveUserSingleDoc = async (uid, collectionName, data) => {
  if (!uid) throw new Error('User not authenticated');

  const ref = doc(db, 'users', uid, collectionName, 'main');
  await setDoc(ref, data, { merge: true });
};

/**
 * ✅ Paginated query helper for user subcollections
 *
 * Path: users/{uid}/{collectionName}/*
 *
 * filters: [{ field, op, value }]
 * order: { field, dir }
 * cursor: last DocumentSnapshot from previous call (returned as lastDoc)
 */
export const getItemsPage = async ({
  uid,
  collectionName,
  filters = [],
  order = { field: 'date', dir: 'desc' },
  pageSize = 50,
  cursor = null,
}) => {
  if (!uid) return { items: [], lastDoc: null, hasMore: false };

  const colRef = collection(db, 'users', uid, collectionName);

  const parts = [];

  // where filters
  for (const f of filters) {
    parts.push(where(f.field, f.op, f.value));
  }

  // order
  if (order?.field) parts.push(orderBy(order.field, order.dir || 'desc'));

  // cursor
  if (cursor) parts.push(startAfter(cursor));

  // limit
  parts.push(limit(pageSize));

  const q = fsQuery(colRef, ...parts);
  const snap = await getDocs(q);

  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const lastDoc = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;

  return { items, lastDoc, hasMore: snap.docs.length === pageSize };
};



